import React, { useEffect, useState } from 'react';
import { Pedido } from '../../../types';
import TableDateFormatter from '../../../../../../components/common/Table/TableDateFormatter';
import { FaInbox } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi2';
import { formatCurrency } from 'views/Restaurant/Gestion/PedidosModifications/utils/ModificationAnalytics';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaClock } from "react-icons/fa";
import PedidoDetail from './components/PedidoDetail/PedidoDetail';

export interface SelectionGroupItem {
  item_id: number;
  item_name: string;
  quantity: number;

}

export interface SelectionGroup {
  selection_group_id: number;
  selected_items: SelectionGroupItem[];
}

export interface Modifier {
  option_id: number;
  option_name: string;
  modifier_id: number;
}

export interface Selection {
  selection_id: string;
  item_id: number;
  item_name: string;
  quantity: number;
  base_price: number;
  selection_groups: SelectionGroup[];
  modifiers: Modifier[];
  total_price: number;
  comment: string;
  clientIndex: number;
}

interface TableProps {
  data: Pedido[];
  pedidosTypeMap?: Record<number, string>;
  onPedidoCancelled?: (pedidoId: number) => void;
}

const Table: React.FC<TableProps> = ({ data, pedidosTypeMap, onPedidoCancelled }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSelections, setSelectedSelections] = useState<Selection[]>([]); // Cambia el tipo a Selection[]
  const [currentPedido, setCurrentPedido] = useState<Pedido | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = (pedido:any) => { // Cambia el tipo a Selection[]
    setSelectedSelections(pedido.selections);
    setCurrentPedido(pedido);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSelections([]);
  };


  const showPedidoStatus = (pedido: Pedido) => {
    if (pedido.cancelled) {
      return <span className="mp-badge mp-badge-danger"><FaTimesCircle /> Cancelado</span>;
    } else if (pedido.closed) {
      return <span className="mp-badge mp-badge-success"><FaCheckCircle /> Pagado</span>;
    } else if (pedido.printed && !pedido.closed && !pedido.cancelled && !pedido.isDelivered && !pedido.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle /> Impreso</span>;
    } else if (!pedido.closed && !pedido.cancelled && !pedido.isDelivered && pedido.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle /> Listo</span>;
    } else if (!pedido.closed && !pedido.cancelled && pedido.isDelivered && pedido.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle /> Entregado</span>;
    } else {
      return <span className="mp-badge mp-badge-info"><FaClock /> Creado</span>;
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.local_timestamp ?? a.timestamp).getTime();
    const dateB = new Date(b.local_timestamp ?? b.timestamp).getTime();
    return dateB - dateA; // más recientes primero
  });

  // Calculate total for a pedido by summing all selections' total_price
  const calculatePedidoTotal = (pedido: Pedido): number => {
    if (!pedido.selections || pedido.selections.length === 0) return 0;
    return pedido.selections.reduce((sum, selection) => sum + (selection.total_price || 0), 0);
  };

  // Helper function to get status class and text for mobile cards
  const getPedidoStatusInfo = (pedido: Pedido) => {
    if (pedido.cancelled) {
      return { className: 'cancelled', text: 'Cancelado' };
    } else if (pedido.closed) {
      return { className: 'success', text: 'Pagado' };
    } else if (pedido.printed && !pedido.closed && !pedido.cancelled && !pedido.isDelivered && !pedido.isReady) {
      return { className: 'warning', text: 'Impreso' };
    } else if (!pedido.closed && !pedido.cancelled && !pedido.isDelivered && pedido.isReady) {
      return { className: 'warning', text: 'Listo' };
    } else if (!pedido.closed && !pedido.cancelled && pedido.isDelivered && pedido.isReady) {
      return { className: 'warning', text: 'Entregado' };
    } else {
      return { className: 'active', text: 'Creado' };
    }
  };

  // Format date/time for mobile cards
  const formatDateOrTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  // Render mobile card view
  const renderMobileView = () => (
    <div className="flex flex-col gap-2.5 p-1">
      {sortedData.map(pedido => {
        const statusInfo = getPedidoStatusInfo(pedido);
        const accentMap: Record<string, string> = {
          success: 'mp-mobile-card-accent-success',
          warning: 'mp-mobile-card-accent-warning',
          cancelled: 'mp-mobile-card-accent-danger',
          active: 'mp-mobile-card-accent-neutral'
        };
        return (
          <div key={pedido.pedido_id} className={`mp-mobile-card ${accentMap[statusInfo.className] || ''}`} onClick={() => handleOpenModal(pedido)}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>
                {pedido.user_name || 'N/A'}
              </span>
              {showPedidoStatus(pedido)}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="text-xs opacity-50" style={{ color: 'var(--text-color)' }}>
                  {formatDateOrTime(pedido.local_timestamp || pedido.timestamp)}
                </span>
                {pedido.mesa_num && <span className="mp-badge mp-badge-warning text-[10px]">Mesa {pedido.mesa_num}</span>}
                {pedido.client_name && <span className="mp-badge mp-badge-info text-[10px]">{pedido.client_name}</span>}
              </div>
              <span className="text-base font-bold tabular-nums flex-shrink-0" style={{ color: '#FC7556' }}>
                {formatCurrency(calculatePedidoTotal(pedido))}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render desktop table view
  const renderDesktopView = () => (
    <div className="mp-table-card">
      <div className="overflow-x-auto overflow-y-auto mp-scrollbar" style={{ maxHeight: '70vh' }}>
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              <th>Cliente</th>
              <th>Usuario</th>
              <th>Mesa</th>
              <th>Turno</th>
              <th>Total</th>
              <th style={{ textAlign: 'center' }}>Estado</th>
              <th>Última actualización</th>
              <th style={{ textAlign: 'right' }}>Detalle</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {sortedData.map((pedido) => (
              <tr key={pedido.pedido_id}>
                <td>{pedido.client_name || <span className="opacity-25 italic">N/A</span>}</td>
                <td className="font-medium">{pedido.user_name || <span className="opacity-25 italic">N/A</span>}</td>
                <td>{pedido.mesa_num || <span className="opacity-25 italic">N/A</span>}</td>
                <td className="text-xs opacity-50">{pedido.shift_name || 'N/A'}</td>
                <td className="font-semibold tabular-nums">{formatCurrency(calculatePedidoTotal(pedido))}</td>
                <td style={{ textAlign: 'center' }}>{showPedidoStatus(pedido)}</td>
                <td className="opacity-60">
                  <TableDateFormatter data={pedido.local_timestamp || pedido.timestamp} isRealTime={false} stopCounting={true} isTimestamp={true} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="mp-btn-icon" onClick={() => handleOpenModal(pedido)} title="Ver detalle">
                    <HiOutlineEye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mp-table-footer">
        <span className="text-xs opacity-35">{sortedData.length} pedidos</span>
      </div>
    </div>
  );


  return (
    <div>
      {sortedData.length === 0 ? (
        <div className="mp-empty-state">
          <FaInbox className="mp-empty-state-icon" />
          <p className="mp-empty-state-text">No hay pedidos en el historial</p>
        </div>
      ) : (
        isMobile ? renderMobileView() : renderDesktopView()
      )}
      <PedidoDetail 
        show={showModal} 
        onClose={handleCloseModal} 
        pedido={currentPedido} 
        pedidosTypeMap={pedidosTypeMap}
        onPedidoCancelled={onPedidoCancelled}
      />
    </div>
  );
};

export default Table;
